import { useState } from 'react';
import type { TourDefinition, TourProgressAdapter, TourCloseReason } from './types';

interface UseOnboardingTourArgs {
  tours: TourDefinition[];
  progressAdapter: TourProgressAdapter;
}

interface UseOnboardingTourResult {
  isMenuOpen: boolean;
  activeTour: TourDefinition | null;
  openMenu(): void;
  closeMenu(): void;
  startTour(id: string): void;
  closeTour(opts: { reason: TourCloseReason }): void;
  hasCompleted(id: string): boolean;
  hasSeen(id: string): boolean;
  resetProgress(): void;
}

export function useOnboardingTour({ tours, progressAdapter }: UseOnboardingTourArgs): UseOnboardingTourResult {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);

  function openMenu() {
    setIsMenuOpen(true);
  }

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function startTour(id: string) {
    const tour = tours.find(t => t.id === id);
    if (!tour) return;
    setIsMenuOpen(false);
    setActiveTour(tour);
  }

  function closeTour({ reason }: { reason: TourCloseReason }) {
    if (activeTour) {
      // 'completed' (pulsó "¡Listo!") → check verde; 'dismissed' (cerró a medias) → solo visto.
      if (reason === 'completed') {
        progressAdapter.markCompleted(activeTour.id);
      } else {
        progressAdapter.markSeen(activeTour.id);
      }
    }
    setActiveTour(null);
  }

  function hasCompleted(id: string): boolean {
    return progressAdapter.hasCompleted(id);
  }

  function hasSeen(id: string): boolean {
    return progressAdapter.hasSeen(id);
  }

  function resetProgress() {
    progressAdapter.reset();
  }

  return {
    isMenuOpen,
    activeTour,
    openMenu,
    closeMenu,
    startTour,
    closeTour,
    hasCompleted,
    hasSeen,
    resetProgress,
  };
}
